import type { ReactNode } from "react";
import { useLingui } from "@lingui/react";
import { AlertTriangle } from "lucide-react";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { ActivityType } from "@/types/activities";
import {
  PICKUP_SOURCE_AUTHORIZED,
  PICKUP_SOURCE_DOOR_MODE,
  PICKUP_SOURCE_NOT_ON_LIST,
  PICKUP_SOURCE_PARENT_NOTICE,
} from "@/schemas/activityPayloadSchemas";
import { ActivityPictures } from "./ActivityPictures";

interface ActivityTypeFieldsProps {
  type: string;
  data: Record<string, unknown>;
}

/**
 * Activity Type Fields Component
 *
 * Renders read-only type-specific field display for view mode
 * Supports all 19 activity types
 */
export function ActivityTypeFields({ type, data }: ActivityTypeFieldsProps) {
  switch (type) {
    case ActivityType.ActivityTypeCheckIn:
      return <CheckInFields data={data} />;

    case ActivityType.ActivityTypeCheckOut:
      return <CheckOutFields data={data} />;

    case ActivityType.ActivityTypeToiletPee:
    case ActivityType.ActivityTypeToiletPoo:
    case ActivityType.ActivityTypeToiletNothing:
      return <ToiletFields data={data} />;

    case ActivityType.ActivityTypeFeedingMilk:
    case ActivityType.ActivityTypeFeedingPorridge:
    case ActivityType.ActivityTypeFeedingSolid:
    case ActivityType.ActivityTypeFeedingSnack:
    case ActivityType.ActivityTypeFeedingBreastmilk:
    case ActivityType.ActivityTypeFeedingRefused:
      return <FeedingFields data={data} feedingType={type} />;

    case ActivityType.ActivityTypeNapStart:
    case ActivityType.ActivityTypeNapEnd:
      return <NapFields data={data} />;

    case ActivityType.ActivityTypeHealthAccident:
      return <AccidentFields data={data} />;

    case ActivityType.ActivityTypeHealthIllness:
      return <IllnessFields data={data} />;

    case ActivityType.ActivityTypeHealthMedication:
      return <MedicationFields data={data} />;

    case ActivityType.ActivityTypePicture:
      return <PictureFields data={data} />;

    case ActivityType.ActivityTypeNote:
      return <NoteFields data={data} />;

    case ActivityType.ActivityTypeMoodCheck:
      return <MoodCheckFields data={data} />;

    default:
      return null;
  }
}

// ============================================================================
// Helper Components
// ============================================================================

function FieldRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-2">
      <div className="font-medium text-muted-foreground">{label}</div>
      <div className="col-span-2">{value || "-"}</div>
    </div>
  );
}

function MoodBadge({ mood }: { mood?: string }) {
  const { i18n } = useLingui();

  if (!mood) return null;

  const moodTones: Record<string, StatusTone> = {
    happy: "success",
    ok: "warning",
    unhappy: "danger",
  };

  const moodLabels: Record<string, string> = {
    happy: i18n._({ id: "activities.mood.happy", message: "Happy" }),
    ok: i18n._({ id: "activities.mood.ok", message: "OK" }),
    unhappy: i18n._({ id: "activities.mood.unhappy", message: "Unhappy" }),
  };

  return <StatusBadge tone={moodTones[mood] ?? "neutral"} label={moodLabels[mood] || mood} />;
}

// ============================================================================
// Type-Specific Field Components
// ============================================================================

function CheckInFields({ data }: { data: Record<string, unknown> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-2">
      {data.mood ? (
        <FieldRow label={i18n._({ id: "activities.form.mood", message: "Mood" })} value={<MoodBadge mood={data.mood as string} />} />
      ) : null}
      {data.notes ? (
        <FieldRow label={i18n._({ id: "activities.form.notes", message: "Notes" })} value={data.notes as string} />
      ) : null}
    </div>
  );
}

function CheckOutFields({ data }: { data: Record<string, unknown> }) {
  const { i18n } = useLingui();

  const source = typeof data.pickup_source === "string" ? data.pickup_source : undefined;
  const doorModeText = i18n._({ id: "activities.pickupSource.doorMode", message: "Self-reported (door mode)" });

  // #2032: one collector row for every source. `picked_up_by` is the pre-#993 field;
  // a door-mode check-out (#2023) has no collector, so the row says so instead.
  const collector =
    (data.pickup_person as string | undefined) ||
    (data.picked_up_by as string | undefined) ||
    (source === PICKUP_SOURCE_DOOR_MODE ? doorModeText : undefined);

  const sourceLabels: Record<string, string> = {
    [PICKUP_SOURCE_AUTHORIZED]: i18n._({ id: "activities.pickupSource.authorizedContact", message: "Authorised contact" }),
    [PICKUP_SOURCE_NOT_ON_LIST]: i18n._({ id: "activities.pickupSource.notOnList", message: "Not on the authorised list" }),
    [PICKUP_SOURCE_PARENT_NOTICE]: i18n._({ id: "activities.pickupSource.parentNotice", message: "Announced by the family" }),
    [PICKUP_SOURCE_DOOR_MODE]: doorModeText,
  };
  const sourceLabel = source ? sourceLabels[source] : undefined;

  return (
    <div className="space-y-2">
      {collector ? (
        <FieldRow
          label={i18n._({ id: "activities.form.pickedUpBy", message: "Picked Up By" })}
          value={
            <>
              <div>{collector}</div>
              {sourceLabel && sourceLabel !== collector ? (
                <div className="text-sm text-muted-foreground">{sourceLabel}</div>
              ) : null}
            </>
          }
        />
      ) : null}
      {source === PICKUP_SOURCE_NOT_ON_LIST && data.pickup_exception_reason ? (
        <FieldRow
          label={i18n._({ id: "activities.pickupException.reason.label", message: "Why were they permitted to?" })}
          value={data.pickup_exception_reason as string}
        />
      ) : null}
      {data.mood ? (
        <FieldRow label={i18n._({ id: "activities.form.mood", message: "Mood" })} value={<MoodBadge mood={data.mood as string} />} />
      ) : null}
    </div>
  );
}

function ToiletFields({ data }: { data: Record<string, unknown> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-2">
      {data.notes ? (
        <FieldRow label={i18n._({ id: "activities.form.notes", message: "Notes" })} value={data.notes as string} />
      ) : null}
    </div>
  );
}

function FeedingFields({ data, feedingType }: { data: Record<string, unknown>; feedingType: string }) {
  const { i18n } = useLingui();

  // Derive food type label from activity type
  const foodTypeLabels: Record<string, string> = {
    [ActivityType.ActivityTypeFeedingMilk]: i18n._({ id: "activities.foodType.milk", message: "Milk" }),
    [ActivityType.ActivityTypeFeedingBreastmilk]: i18n._({ id: "activities.foodType.breastmilk", message: "Breastmilk" }),
    [ActivityType.ActivityTypeFeedingPorridge]: i18n._({ id: "activities.foodType.porridge", message: "Porridge" }),
    [ActivityType.ActivityTypeFeedingSolid]: i18n._({ id: "activities.foodType.solid", message: "Solid Food" }),
    [ActivityType.ActivityTypeFeedingSnack]: i18n._({ id: "activities.foodType.snack", message: "Snack" }),
    [ActivityType.ActivityTypeFeedingRefused]: i18n._({ id: "activities.foodType.refused", message: "Refused" }),
  };

  const quantityCommentLabels: Record<string, string> = {
    ok: i18n._({ id: "activities.quantityComment.ok", message: "OK" }),
    few: i18n._({ id: "activities.quantityComment.few", message: "Few" }),
    much: i18n._({ id: "activities.quantityComment.much", message: "Much" }),
  };

  const foodTypeLabel = foodTypeLabels[feedingType] || feedingType;

  return (
    <div className="space-y-2">
      <FieldRow label={i18n._({ id: "activities.form.foodType", message: "Food Type" })} value={foodTypeLabel} />
      {data.quantity_ml ? (
        <FieldRow label={i18n._({ id: "activities.form.quantityMl", message: "Quantity (ml)" })} value={`${data.quantity_ml} ml`} />
      ) : null}
      {data.quantity_comment ? (
        <FieldRow
          label={i18n._({ id: "activities.form.quantityComment", message: "Quantity" })}
          value={quantityCommentLabels[data.quantity_comment as string] || (data.quantity_comment as string)}
        />
      ) : null}
    </div>
  );
}

function NapFields({ data }: { data: Record<string, unknown> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-2">
      {data.notes ? (
        <FieldRow label={i18n._({ id: "activities.form.notes", message: "Notes" })} value={data.notes as string} />
      ) : null}
    </div>
  );
}

function AccidentFields({ data }: { data: Record<string, unknown> }) {
  const { i18n } = useLingui();

  const severityTones: Record<string, StatusTone> = {
    minor: "info",
    moderate: "warning",
    serious: "danger",
    critical: "danger",
  };

  const severityLabels: Record<string, string> = {
    minor: i18n._({ id: "activities.severity.minor", message: "Minor" }),
    moderate: i18n._({ id: "activities.severity.moderate", message: "Moderate" }),
    serious: i18n._({ id: "activities.severity.serious", message: "Serious" }),
    critical: i18n._({ id: "activities.severity.critical", message: "Critical" }),
  };


  return (
    <div className="space-y-2">
      <FieldRow
        label={i18n._({ id: "activities.form.severity", message: "Severity" })}
        value={
          <StatusBadge
            tone={severityTones[data.severity as string] ?? "neutral"}
            // Serious and critical share the danger tone; the icon sets critical apart.
            icon={data.severity === "critical" ? AlertTriangle : undefined}
            label={severityLabels[data.severity as string] || (data.severity as string)}
          />
        }
      />
      <FieldRow label={i18n._({ id: "activities.form.courseOfAccident", message: "Course of Accident" })} value={data.course_of_accident as string} />
      {data.incidents && Array.isArray(data.incidents) && data.incidents.length > 0 ? (
        <FieldRow
          label={i18n._({ id: "activities.form.incidents", message: "Incidents" })}
          value={
            <ul className="list-disc list-inside">
              {(data.incidents as string[]).map((incident, idx) => (
                <li key={idx}>{incident}</li>
              ))}
            </ul>
          }
        />
      ) : null}
      {data.measures && Array.isArray(data.measures) && data.measures.length > 0 ? (
        <FieldRow
          label={i18n._({ id: "activities.form.measuresTaken", message: "Measures Taken" })}
          value={
            <ul className="list-disc list-inside">
              {(data.measures as string[]).map((measure, idx) => (
                <li key={idx}>{measure}</li>
              ))}
            </ul>
          }
        />
      ) : null}
      {data.place_of_accident ? (
        <FieldRow label={i18n._({ id: "activities.form.placeOfAccident", message: "Place of Accident" })} value={data.place_of_accident as string} />
      ) : null}
      {data.observer ? (
        <FieldRow label={i18n._({ id: "activities.form.observer", message: "Observer" })} value={data.observer as string} />
      ) : null}
      {data.perpetrator ? (
        <FieldRow label={i18n._({ id: "activities.form.perpetrator", message: "Perpetrator" })} value={data.perpetrator as string} />
      ) : null}
      {data.prevention_measures ? (
        <FieldRow label={i18n._({ id: "activities.form.preventionMeasures", message: "Prevention Measures" })} value={data.prevention_measures as string} />
      ) : null}
      {data.mood ? (
        <FieldRow label={i18n._({ id: "activities.form.mood", message: "Mood" })} value={<MoodBadge mood={data.mood as string} />} />
      ) : null}
      {data.pictures && Array.isArray(data.pictures) && data.pictures.length > 0 ? (
        <div className="col-span-3 mt-4">
          <div className="font-medium text-muted-foreground mb-2">{i18n._({ id: "activities.form.pictures", message: "Pictures" })}</div>
          <ActivityPictures pictureUrls={data.pictures as string[]} />
        </div>
      ) : null}
      {data.notes ? (
        <FieldRow label={i18n._({ id: "activities.form.notes", message: "Notes" })} value={data.notes as string} />
      ) : null}
    </div>
  );
}

function IllnessFields({ data }: { data: Record<string, unknown> }) {
  const { i18n } = useLingui();

  const contagiousTones: Record<string, StatusTone> = {
    yes: "danger",
    no: "success",
    maybe: "warning",
  };

  const contagiousLabels: Record<string, string> = {
    yes: i18n._({ id: "activities.contagious.yes", message: "Yes" }),
    no: i18n._({ id: "activities.contagious.no", message: "No" }),
    maybe: i18n._({ id: "activities.contagious.maybe", message: "Maybe" }),
  };


  return (
    <div className="space-y-2">
      {data.illnesses && Array.isArray(data.illnesses) && data.illnesses.length > 0 ? (
        <FieldRow
          label={i18n._({ id: "activities.form.illnesses", message: "Illnesses" })}
          value={
            <ul className="list-disc list-inside">
              {(data.illnesses as string[]).map((illness, idx) => (
                <li key={idx}>{illness}</li>
              ))}
            </ul>
          }
        />
      ) : null}
      <FieldRow
        label={i18n._({ id: "activities.form.contagious", message: "Contagious" })}
        value={
          <StatusBadge
            tone={contagiousTones[data.contagious as string] ?? "neutral"}
            label={contagiousLabels[data.contagious as string] || (data.contagious as string)}
          />
        }
      />
      {data.symptoms ? (
        <FieldRow label={i18n._({ id: "activities.form.symptoms", message: "Symptoms" })} value={data.symptoms as string} />
      ) : null}
      {data.measures ? (
        <FieldRow label={i18n._({ id: "activities.form.measures", message: "Measures" })} value={data.measures as string} />
      ) : null}
      {data.mood ? (
        <FieldRow label={i18n._({ id: "activities.form.mood", message: "Mood" })} value={<MoodBadge mood={data.mood as string} />} />
      ) : null}
      {data.notes ? (
        <FieldRow label={i18n._({ id: "activities.form.notes", message: "Notes" })} value={data.notes as string} />
      ) : null}
    </div>
  );
}

function MedicationFields({ data }: { data: Record<string, unknown> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-2">
      <FieldRow label={i18n._({ id: "activities.form.medicationAdministered", message: "Medication Administered" })} value={data.medication_administered as string} />
      {data.mood ? (
        <FieldRow label={i18n._({ id: "activities.form.mood", message: "Mood" })} value={<MoodBadge mood={data.mood as string} />} />
      ) : null}
      {data.notes ? (
        <FieldRow label={i18n._({ id: "activities.form.notes", message: "Notes" })} value={data.notes as string} />
      ) : null}
    </div>
  );
}

function PictureFields({ data }: { data: Record<string, unknown> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-2">
      {data.picture_url ? (
        <div className="col-span-3 mt-4">
          <div className="font-medium text-muted-foreground mb-2">{i18n._({ id: "activities.form.picture", message: "Picture" })}</div>
          <ActivityPictures pictureUrls={[data.picture_url as string]} />
        </div>
      ) : null}
    </div>
  );
}

function NoteFields({ data }: { data: Record<string, unknown> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-2">
      {data.subject ? (
        <FieldRow label={i18n._({ id: "activities.form.subject", message: "Subject" })} value={data.subject as string} />
      ) : null}
      {data.content ? (
        <FieldRow label={i18n._({ id: "activities.form.content", message: "Content" })} value={data.content as string} />
      ) : null}
    </div>
  );
}

function MoodCheckFields({ data }: { data: Record<string, unknown> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-2">
      {data.mood ? (
        <FieldRow label={i18n._({ id: "activities.form.mood", message: "Mood" })} value={<MoodBadge mood={data.mood as string} />} />
      ) : null}
      {data.notes ? (
        <FieldRow label={i18n._({ id: "activities.form.notes", message: "Notes" })} value={data.notes as string} />
      ) : null}
    </div>
  );
}
